import React, { useState, useEffect, useRef } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileText, 
  History, 
  RotateCcw, 
  Save, 
  ChevronDown, 
  ChevronUp,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Volume2,
  Pause,
  Play,
  Mic,
  Search,
  RefreshCw,
  MessageSquare,
  HelpCircle,
  X,
  Building2,
  Loader2,
  Sparkles,
  Wand2,
  Braces,
  Code2
} from 'lucide-react';

export default function VoicePrompts() {
  const { fetchApi } = useCloudAuth();
  const { currentProfile, loading: profileLoading } = useBusinessProfile();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showPromptHelper, setShowPromptHelper] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // AI Generation state
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [aiFormData, setAiFormData] = useState({
    businessName: '',
    businessDescription: '',
    agentName: '',
    services: '',
    targetAudience: '',
    tone: 'friendly',
    additionalInfo: ''
  });
  
  // Simplified form state - just 2 main fields
  const [formData, setFormData] = useState({
    welcomeMessage: '',
    systemPrompt: '',
    voiceId: '',
    tone: 'friendly',
    changeNote: ''
  });
  
  // Voice selection state
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voiceSearch, setVoiceSearch] = useState('');
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const audioRef = useRef(null);

  // Custom API variables
  const [apiIntegrations, setApiIntegrations] = useState([]);
  const [lastFocusedField, setLastFocusedField] = useState('systemPrompt');
  const systemPromptRef = useRef(null);
  const welcomeMessageRef = useRef(null);

  // Reload data when business profile changes
  useEffect(() => {
    if (currentProfile?.id) {
      loadPromptData();
      loadVoices();
    }
  }, [currentProfile?.id]);

  const loadVoices = async () => {
    try {
      setVoicesLoading(true);
      const res = await fetchApi('/api/voice/voices');
      if (res.success && res.data) {
        setVoices(res.data);
      }
    } catch (err) {
      console.error('Failed to load voices:', err);
    } finally {
      setVoicesLoading(false);
    }
  };

  // Resolve a stored voice_id (may be legacy ElevenLabs ID) to a display name
  const resolveVoiceName = (voiceId) => {
    if (!voiceId) return 'Not selected';
    const match = voices.find(v => v.voice_id === voiceId);
    if (match) return match.name;
    const LEGACY = {
      'EXAVITQu4vr4xnSDxMaL': 'Nova', '21m00Tcm4TlvDq8ikWAM': 'Alloy',
      'pNInz6obpgDQGcFmaJgB': 'Echo',  'jBpfuIE2acCO8z3wKNLl': 'Fable',
      'onwK4e9ZLuTAKqWW03F9': 'Onyx',  'XB0fDUnXU5powFXDhCwa': 'Shimmer',
    };
    return LEGACY[voiceId] || 'Nova';
  };

  const playVoicePreview = async (voiceId) => {
    // Toggle off if already playing this voice
    if (playingVoiceId === voiceId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoiceId(null);

    try {
      setPreviewLoading(voiceId);
      // Fetch audio via the API (handles auth + generates TTS on demand)
      const API_GATEWAY = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('cloud_auth_token');
      const res = await fetch(`${API_GATEWAY}/api/voice/preview/${voiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Preview failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const audio = new Audio(url);
      audio.onended = () => { setPlayingVoiceId(null); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPlayingVoiceId(null); URL.revokeObjectURL(url); };
      audio.play();
      audioRef.current = audio;
      setPlayingVoiceId(voiceId);
    } catch (err) {
      console.error('Voice preview error:', err);
      setPlayingVoiceId(null);
    } finally {
      setPreviewLoading(null);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const loadPromptData = async () => {
    if (!currentProfile?.id) return;
    
    try {
      setLoading(true);
      const profileParam = `businessProfileId=${currentProfile.id}`;
      const [promptRes, versionsRes, apisRes] = await Promise.all([
        fetchApi(`/api/voice/prompts/current?${profileParam}`),
        fetchApi(`/api/voice/prompts/versions?limit=10&${profileParam}`),
        fetchApi(`/api/cloud/integrations/api-integrations?${profileParam}`).catch(() => ({ data: null })),
      ]);
      
      setCurrentPrompt(promptRes.data);
      setVersions(versionsRes.data.versions || []);
      // API integrations: response.data is directly the array
      const integrations = apisRes.data;
      setApiIntegrations(Array.isArray(integrations) ? integrations : integrations?.integrations || []);
      
      if (promptRes.data?.prompt) {
        const p = promptRes.data.prompt;
        // Combine old fields into system prompt if they exist
        let combinedPrompt = p.systemPrompt || '';
        if (p.businessContext) {
          combinedPrompt += `\n\nBusiness Context:\n${p.businessContext}`;
        }
        if (p.customInstructions) {
          combinedPrompt += `\n\nAdditional Instructions:\n${p.customInstructions}`;
        }
        
        setFormData({
          welcomeMessage: p.welcomeMessage || '',
          systemPrompt: combinedPrompt.trim(),
          voiceId: p.voiceId || '',
          tone: p.tone || 'friendly',
          changeNote: ''
        });
      } else {
        // Reset to defaults for a new business profile
        setFormData({
          welcomeMessage: '',
          systemPrompt: '',
          voiceId: '',
          tone: 'friendly',
          changeNote: ''
        });
      }
    } catch (err) {
      setError('Failed to load prompt data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.systemPrompt.trim()) {
      setError('System prompt is required');
      return;
    }
    
    if (!currentProfile?.id) {
      setError('No business profile selected');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const payload = {
        systemPrompt: formData.systemPrompt,
        welcomeMessage: formData.welcomeMessage || null,
        voiceId: formData.voiceId || null,
        changeNote: formData.changeNote || null,
        tone: formData.tone || 'friendly',
        language: 'en-US',
        businessContext: null,
        customInstructions: null,
        guardrails: null,
        businessProfileId: currentProfile.id
      };
      
      const res = await fetchApi('/api/voice/prompts/update', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      setSuccessMessage(`Version ${res.data.version.version} saved successfully!`);
      setFormData(prev => ({ ...prev, changeNote: '' }));
      
      await loadPromptData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to save prompt');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (versionId, versionNum) => {
    if (!window.confirm(`Rollback to version ${versionNum}? This will create a new version with that content.`)) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      await fetchApi('/api/voice/prompts/rollback', {
        method: 'POST',
        body: JSON.stringify({
          versionId,
          reason: 'Manual rollback from UI'
        })
      });
      
      setSuccessMessage(`Rolled back to version ${versionNum}!`);
      await loadPromptData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to rollback');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Insert a variable token at the cursor position of the last focused textarea
  const insertVariable = (variableName, targetField = null) => {
    const field = targetField || lastFocusedField;
    const ref = field === 'systemPrompt' ? systemPromptRef : welcomeMessageRef;
    const el = ref.current;
    if (!el) {
      // Fallback: just append to the field
      handleInputChange(field, (formData[field] || '') + `{${variableName}}`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const token = `{${variableName}}`;
    const current = formData[field] || '';
    const newText = current.substring(0, start) + token + current.substring(end);
    handleInputChange(field, newText);
    // Restore cursor right after the inserted token
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  // AI Prompt Generation
  const handleGeneratePrompt = async () => {
    if (!aiFormData.businessDescription.trim()) {
      setError('Please describe your business');
      return;
    }
    
    try {
      setGenerating(true);
      setError(null);
      
      const res = await fetchApi('/api/voice/prompts/generate', {
        method: 'POST',
        body: JSON.stringify({
          businessName: aiFormData.businessName || currentProfile?.name,
          businessDescription: aiFormData.businessDescription,
          agentName: aiFormData.agentName,
          services: aiFormData.services,
          targetAudience: aiFormData.targetAudience,
          tone: aiFormData.tone,
          additionalInfo: aiFormData.additionalInfo
        })
      });
      
      if (res.success && res.data) {
        setGeneratedPrompt(res.data);
      }
    } catch (err) {
      setError('Failed to generate prompt. Please try again.');
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const applyGeneratedPrompt = () => {
    if (generatedPrompt) {
      setFormData(prev => ({
        ...prev,
        systemPrompt: generatedPrompt.systemPrompt,
        welcomeMessage: generatedPrompt.welcomeMessage || prev.welcomeMessage
      }));
      setShowAIGenerator(false);
      setGeneratedPrompt(null);
      setSuccessMessage('AI-generated prompt applied! Review and save when ready.');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const resetAIGenerator = () => {
    setGeneratedPrompt(null);
    setAiFormData({
      businessName: currentProfile?.name || '',
      businessDescription: '',
      agentName: '',
      services: '',
      targetAudience: '',
      tone: 'friendly',
      additionalInfo: ''
    });
  };

  if (profileLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Business Profile Selected</h3>
        <p className="text-gray-400">Select a business profile from the dropdown above to configure prompts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="voice-prompts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-400" />
            Agent Configuration for {currentProfile.name}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure what your AI voice agent says and how it behaves
          </p>
        </div>
        <div className="flex items-center gap-3">
          {currentPrompt?.version > 0 && (
            <span className="text-sm text-gray-400">
              v{currentPrompt.version}
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="save-prompt-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{successMessage}</span>
        </div>
      )}

      {/* Welcome Message */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <MessageSquare className="w-5 h-5 text-green-400" />
            Welcome Message
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {apiIntegrations.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-800/60 border border-gray-700/50 rounded-lg">
              <span className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                <Braces className="w-3 h-3" /> Insert variable:
              </span>
              {apiIntegrations.map((api) => (
                <button
                  key={api._id || api.id}
                  onClick={() => insertVariable(api.variableName, 'welcomeMessage')}
                  title={`Insert {${api.variableName}} — ${api.name}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25 hover:border-indigo-400/50 transition-all text-xs font-mono cursor-pointer"
                >
                  <Code2 className="w-3 h-3" />
                  {`{${api.variableName}}`}
                </button>
              ))}
            </div>
          )}
          <textarea
            ref={welcomeMessageRef}
            value={formData.welcomeMessage}
            onChange={(e) => handleInputChange('welcomeMessage', e.target.value)}
            onFocus={() => setLastFocusedField('welcomeMessage')}
            className="w-full h-20 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            placeholder="Hello! Thank you for calling [Company]. How can I help you today?"
            data-testid="welcome-message-input"
          />
          <p className="text-xs text-gray-500">
            This is the first thing callers hear when they connect. Keep it short and welcoming (under 10 seconds).
          </p>
        </CardContent>
      </Card>

      {/* Agent Tone */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <User className="w-5 h-5 text-blue-400" />
            Agent Personality
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'friendly', label: 'Friendly', desc: 'Warm, casual, uses first names sparingly', emoji: '😊' },
              { id: 'professional', label: 'Professional', desc: 'Formal, business-focused, polite', emoji: '👔' },
              { id: 'casual', label: 'Casual', desc: 'Relaxed, conversational, laid-back', emoji: '😎' }
            ].map((tone) => (
              <button
                key={tone.id}
                onClick={() => handleInputChange('tone', tone.id)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  formData.tone === tone.id
                    ? 'bg-purple-500/20 border-purple-500/50 ring-2 ring-purple-500/30'
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{tone.emoji}</span>
                  <span className="font-medium text-white">{tone.label}</span>
                </div>
                <p className="text-xs text-gray-400">{tone.desc}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Choose how your agent sounds. This affects word choice and conversation style.
          </p>
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-purple-400" />
              System Prompt
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowAIGenerator(true);
                  setAiFormData(prev => ({ ...prev, businessName: currentProfile?.name || '' }));
                }}
                className="flex items-center gap-1 text-xs bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-3 py-1.5 rounded-full transition-all"
              >
                <Sparkles className="w-3 h-3" />
                Generate with AI
              </button>
              <button
                onClick={() => setShowPromptHelper(!showPromptHelper)}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                {showPromptHelper ? 'Hide Example' : 'Show Example'}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Prompt Helper/Example */}
          {showPromptHelper && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-sm font-medium text-purple-300">Example: Customer Service Agent</h4>
                <button 
                  onClick={() => setShowPromptHelper(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono bg-gray-800/50 p-3 rounded overflow-x-auto">
{`You are Sarah, a friendly customer service representative for TechCorp Solutions.

ROLE & PERSONALITY:
- Warm, helpful, and professional
- Patient with customers, never frustrated
- Speak naturally, like a real person (use contractions, casual phrases)

WHAT YOU CAN HELP WITH:
- Product information and pricing
- Order status inquiries
- Technical support questions
- Scheduling appointments or demos
- General company inquiries

GUIDELINES:
- Keep responses brief (1-2 sentences max for voice)
- Ask one question at a time
- Always confirm important details (order numbers, dates, names)
- If you don't know something, offer to transfer to a specialist

BUSINESS INFO:
- Hours: Monday-Friday, 9 AM - 6 PM EST
- Website: techcorp.com
- Support email: help@techcorp.com

SAMPLE RESPONSES:
- "Hi! Thanks for calling TechCorp. How can I help you today?"
- "I'd be happy to check on that order for you. Could you give me the order number?"
- "Let me transfer you to our technical team for that. One moment please."`}
              </pre>
              <button
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    systemPrompt: `You are [Agent Name], a friendly customer service representative for [Your Company].

ROLE & PERSONALITY:
- Warm, helpful, and professional
- Patient with customers, never frustrated
- Speak naturally, like a real person

WHAT YOU CAN HELP WITH:
- Product information and pricing
- Order status inquiries
- Technical support questions
- Scheduling appointments or demos
- General company inquiries

GUIDELINES:
- Keep responses brief (1-2 sentences max for voice)
- Ask one question at a time
- Always confirm important details
- If you don't know something, offer to transfer to a specialist

BUSINESS INFO:
- Hours: [Your business hours]
- Website: [Your website]
- Phone: [Your phone number]`
                  }));
                  setShowPromptHelper(false);
                }}
                className="mt-3 text-xs bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded transition-colors"
              >
                Use This Template
              </button>
            </div>
          )}

          {/* Variable Picker — only shown when Custom APIs are configured */}
          {apiIntegrations.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-2.5 bg-gray-800/60 border border-indigo-500/20 rounded-lg">
              <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0 font-medium">
                <Braces className="w-3 h-3 text-indigo-400" />
                Custom API variables:
              </span>
              {apiIntegrations.map((api) => (
                <button
                  key={api._id || api.id}
                  onClick={() => insertVariable(api.variableName, 'systemPrompt')}
                  title={`${api.name} — click to insert {${api.variableName}} at cursor`}
                  data-testid={`variable-chip-${api.variableName}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/30 hover:border-indigo-400/60 hover:text-indigo-200 active:scale-95 transition-all text-xs font-mono cursor-pointer select-none"
                >
                  <Code2 className="w-3 h-3 shrink-0" />
                  {`{${api.variableName}}`}
                  <span className="text-gray-500 font-sans not-italic ml-0.5 text-[10px]">
                    — {api.name}
                  </span>
                </button>
              ))}
              <span className="text-[10px] text-gray-600 ml-auto hidden sm:block">
                Click to insert at cursor
              </span>
            </div>
          )}
          
          <textarea
            ref={systemPromptRef}
            value={formData.systemPrompt}
            onChange={(e) => handleInputChange('systemPrompt', e.target.value)}
            onFocus={() => setLastFocusedField('systemPrompt')}
            className="w-full h-64 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
            placeholder={`You are a helpful AI voice assistant for [Company Name].

Your role is to assist callers with their questions and requests.

Guidelines:
- Be friendly and professional
- Keep responses brief (1-2 sentences)
- Ask clarifying questions when needed
- Always confirm important details

Business Information:
- Company hours: Monday-Friday, 9am-5pm
- Main services: ...`}
            data-testid="system-prompt-input"
          />
          <p className="text-xs text-gray-500">
            Define your agent's personality, knowledge, and behavior. Include any business context, special instructions, or guidelines here.
            {apiIntegrations.length > 0 && (
              <span className="text-indigo-400 ml-1">
                Use <code className="bg-gray-800 px-1 rounded">{'{variable}'}</code> tokens to let the AI pull live data from your Custom APIs.
              </span>
            )}
          </p>
        </CardContent>
      </Card>

      {/* Voice Selection */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Volume2 className="w-5 h-5 text-amber-400" />
            Agent Voice
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Voice Display */}
          {formData.voiceId && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {resolveVoiceName(formData.voiceId)}
                  </p>
                  {voices.find(v => v.voice_id === formData.voiceId)?.description && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {voices.find(v => v.voice_id === formData.voiceId)?.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => playVoicePreview(formData.voiceId)}
                  disabled={previewLoading === formData.voiceId}
                  className={`h-8 w-8 p-0 ${playingVoiceId === formData.voiceId ? 'text-green-400' : 'text-gray-400 hover:text-white'}`}
                  title="Preview current voice"
                >
                  {previewLoading === formData.voiceId ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : playingVoiceId === formData.voiceId ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleInputChange('voiceId', '')}
                  className="text-gray-400 hover:text-red-400"
                >
                  Change
                </Button>
              </div>
            </div>
          )}

          {/* Search */}
          {!formData.voiceId && (
            <>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={voiceSearch}
                    onChange={(e) => setVoiceSearch(e.target.value)}
                    placeholder="Search voices..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={loadVoices}
                  disabled={voicesLoading}
                  className="px-3"
                >
                  <RefreshCw className={`w-4 h-4 ${voicesLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Voice List */}
              {voicesLoading ? (
                <div className="text-center py-8 text-gray-400">Loading voices...</div>
              ) : voices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mic className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No voices available.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-2">
                  {voices
                    .filter(v => {
                      const matchesSearch = voiceSearch === '' || 
                        v.name?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
                        v.description?.toLowerCase().includes(voiceSearch.toLowerCase());
                      return matchesSearch;
                    })
                    .map((voice) => {
                      const isSelected = formData.voiceId === voice.voice_id;
                      const isPlaying = playingVoiceId === voice.voice_id;
                      const labels = voice.labels || {};
                      
                      return (
                        <div
                          key={voice.voice_id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-purple-500/20 border-purple-500/50' 
                              : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                          }`}
                          onClick={() => handleInputChange('voiceId', voice.voice_id)}
                          data-testid={`voice-${voice.voice_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${isSelected ? 'text-purple-300' : 'text-white'}`}>
                                  {voice.name}
                                </span>
                                {isSelected && (
                                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {labels.gender && (
                                  <span className="text-xs px-1.5 py-0.5 bg-gray-700 rounded text-gray-300">
                                    {labels.gender}
                                  </span>
                                )}
                                {labels.accent && (
                                  <span className="text-xs px-1.5 py-0.5 bg-gray-700 rounded text-gray-300">
                                    {labels.accent}
                                  </span>
                                )}
                              </div>
                              {voice.description && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                  {voice.description}
                                </p>
                              )}
                            </div>
                            {/* Play button — always visible, fetches preview on demand */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                playVoicePreview(voice.voice_id);
                              }}
                              disabled={previewLoading === voice.voice_id}
                              className={`ml-2 flex-shrink-0 h-8 w-8 p-0 ${
                                isPlaying ? 'text-green-400' : 'text-gray-400 hover:text-white'
                              }`}
                              data-testid={`play-voice-${voice.voice_id}`}
                              title={isPlaying ? 'Stop preview' : 'Preview voice'}
                            >
                              {previewLoading === voice.voice_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : isPlaying ? (
                                <Pause className="w-4 h-4" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Change Note */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="pt-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Change Note (optional)
            </label>
            <input
              type="text"
              value={formData.changeNote}
              onChange={(e) => handleInputChange('changeNote', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="What changed in this version?"
              data-testid="change-note-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Version History */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setShowVersions(!showVersions)}
        >
          <CardTitle className="text-white flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Version History ({versions.length})
            </div>
            {showVersions ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </CardTitle>
        </CardHeader>
        {showVersions && (
          <CardContent>
            {versions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No versions yet. Save your first prompt to create a version.</p>
            ) : (
              <div className="space-y-3">
                {versions.map((version) => (
                  <div 
                    key={version.id}
                    className={`p-4 rounded-lg border ${
                      version.isActive 
                        ? 'bg-purple-500/10 border-purple-500/30' 
                        : 'bg-gray-800/50 border-gray-700'
                    }`}
                    data-testid={`version-${version.version}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-white">
                          v{version.version}
                        </span>
                        {version.isActive && (
                          <span className="px-2 py-1 text-xs font-medium bg-purple-500/20 text-purple-400 rounded">
                            Active
                          </span>
                        )}
                        {version.isRollback && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-500/20 text-blue-400 rounded">
                            Rollback from v{version.rolledBackVersion}
                          </span>
                        )}
                      </div>
                      {!version.isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRollback(version.id, version.version)}
                          className="text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                          data-testid={`rollback-btn-${version.version}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Rollback
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(version.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {version.createdBy && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {version.createdBy}
                        </span>
                      )}
                    </div>
                    {version.changeNote && (
                      <p className="mt-2 text-sm text-gray-300">
                        "{version.changeNote}"
                      </p>
                    )}
                    <p className="mt-2 text-sm text-gray-500 truncate">
                      {version.systemPrompt?.substring(0, 100)}...
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* AI Prompt Generator Modal */}
      {showAIGenerator && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                    <Wand2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Generate Prompt with AI</h2>
                    <p className="text-sm text-gray-400">Describe your business and we'll create a professional prompt</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAIGenerator(false);
                    resetAIGenerator();
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {!generatedPrompt ? (
                <div className="space-y-4">
                  {/* Business Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={aiFormData.businessName}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, businessName: e.target.value }))}
                      placeholder="e.g., ABC Company, Joe's Pizza"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>

                  {/* Business Description - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Business Description <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={aiFormData.businessDescription}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, businessDescription: e.target.value }))}
                      placeholder="Describe what your business does, your main products/services, and what makes you unique. The more detail you provide, the better the generated prompt will be."
                      rows={4}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 resize-none"
                    />
                  </div>

                  {/* Agent Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      AI Agent Name (optional)
                    </label>
                    <input
                      type="text"
                      value={aiFormData.agentName}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, agentName: e.target.value }))}
                      placeholder="e.g., Sarah, Max, Alex"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate a name</p>
                  </div>

                  {/* Services */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Services/Products (optional)
                    </label>
                    <input
                      type="text"
                      value={aiFormData.services}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, services: e.target.value }))}
                      placeholder="e.g., Web development, Pizza delivery, Legal consultations"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                    />
                  </div>

                  {/* Tone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Conversation Tone
                    </label>
                    <select
                      value={aiFormData.tone}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, tone: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="friendly">Friendly & Casual</option>
                      <option value="professional">Professional & Formal</option>
                      <option value="casual">Very Casual</option>
                      <option value="enthusiastic">Enthusiastic & Energetic</option>
                    </select>
                  </div>

                  {/* Additional Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Additional Instructions (optional)
                    </label>
                    <textarea
                      value={aiFormData.additionalInfo}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                      placeholder="Any specific instructions, business hours, special policies, or things the AI should know..."
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 resize-none"
                    />
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGeneratePrompt}
                    disabled={generating || !aiFormData.businessDescription.trim()}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate Prompt
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Generated Welcome Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Generated Welcome Message
                    </label>
                    <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm">
                      {generatedPrompt.welcomeMessage}
                    </div>
                  </div>

                  {/* Generated System Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Generated System Prompt
                    </label>
                    <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                      {generatedPrompt.systemPrompt}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={resetAIGenerator}
                      className="flex-1 py-3 border border-gray-700 text-gray-300 hover:bg-gray-800 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Generate Again
                    </button>
                    <button
                      onClick={applyGeneratedPrompt}
                      className="flex-1 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Apply Prompt
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Click "Apply Prompt" to use this generated content. You can edit it before saving.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
